import React from 'react';
import { FaCheck, FaTimes } from 'react-icons/fa';
import { EmotionDto } from '../interfaces/emotion';
import { FlowDto } from '../interfaces/flow';
import { PeriodDto } from '../interfaces/period';
import { SymptomDto } from '../interfaces/symptom';
import EmotionSelector from './EmotionSelector';
import FlowSelector from './FlowSelector';
import Loader from './Loader';
import SymptomSelector from './SymptomSelector';

interface CalendarDayModalProps {
    isLoading: boolean;
    error?: string | null;
    selectedDate: Date;
    periodForDay: PeriodDto | null;
    isFirstDay: boolean;
    periodAction: {
        action: 'none' | 'extend' | 'ask' | 'associate' | 'create';
        period?: PeriodDto;
        direction?: 'forward' | 'backward';
        diffDays?: number;
    };
    showDeleteWarning: boolean;
    isEditingPeriod: boolean;
    isSavingPeriod: boolean;
    isDeleting: boolean;
    isSavingFlow: boolean;
    isDeletingFlow: boolean;
    isSavingSymptoms: boolean;
    isSavingEmotions: boolean;
    flowSaveStatus: 'idle' | 'success' | 'error';
    symptomsSaveStatus: 'idle' | 'success' | 'error';
    emotionsSaveStatus: 'idle' | 'success' | 'error';
    flowLevel: FlowDto | null;
    existingFlow: FlowDto | null;
    selectedSymptoms: number[];
    existingSymptoms: number[];
    selectedEmotions: number[];
    existingEmotions: number[];
    flujosCatalogo: FlowDto[];
    sintomasCatalogo: SymptomDto[];
    emocionesCatalogo: EmotionDto[];
    onChangeFlowLevel: (flow: FlowDto | null) => void;
    onSaveFlow: () => void;
    onDeleteFlow: () => void;
    onChangeSymptoms: (ids: number[]) => void;
    onSaveSymptoms: () => void;
    onChangeEmotions: (ids: number[]) => void;
    onSaveEmotions: () => void;
    onCreatePeriod: () => void;
    onExtendPeriod: (options: { period: PeriodDto; direction: 'forward' | 'backward' }) => void;
    onEditPeriod: (options: { setStart?: boolean; removeDay?: boolean }) => void;
    onDeletePeriod: () => void;
    onShowDeleteWarning: (show: boolean) => void;
    pendingPeriodEdit: {
        title: string;
        description: string;
        confirmLabel: string;
        isDanger?: boolean;
    } | null;
    onConfirmPendingPeriodEdit: () => void;
    onCancelPendingPeriodEdit: () => void;
}

export const CalendarDayModal: React.FC<CalendarDayModalProps> = ({
    isLoading,
    error,
    selectedDate,
    periodForDay,
    isFirstDay,
    periodAction,
    showDeleteWarning,
    isEditingPeriod,
    isSavingPeriod,
    isDeleting,
    isSavingFlow,
    isDeletingFlow,
    isSavingSymptoms,
    isSavingEmotions,
    flowSaveStatus,
    symptomsSaveStatus,
    emotionsSaveStatus,
    flowLevel,
    existingFlow,
    selectedSymptoms,
    existingSymptoms,
    selectedEmotions,
    existingEmotions,
    flujosCatalogo,
    sintomasCatalogo,
    emocionesCatalogo,
    onChangeFlowLevel,
    onSaveFlow,
    onDeleteFlow,
    onChangeSymptoms,
    onSaveSymptoms,
    onChangeEmotions,
    onSaveEmotions,
    onCreatePeriod,
    onExtendPeriod,
    onEditPeriod,
    onDeletePeriod,
    onShowDeleteWarning,
    pendingPeriodEdit,
    onConfirmPendingPeriodEdit,
    onCancelPendingPeriodEdit,
}) => {
    if (isLoading) {
        return <Loader text='Cargando datos del día...' />;
    }

    const renderButtonContent = (status: 'idle' | 'success' | 'error', defaultLabel: string) => {
        if (status === 'success') {
            return <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <FaCheck color='#16a34a' />
                <span>Guardado!</span>
            </span>;
        }
        if (status === 'error') {
            return (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    <FaTimes color='#dc2626' />
                    <span>Intenta Nuevamente</span>
                </span>
            );
        }
        return <span>{defaultLabel}</span>;
    };

    const DAY_IN_MS = 24 * 60 * 60 * 1000;
    const formatDateLabel = (date: Date) =>
        date.toLocaleDateString('es-ES', {
            day: '2-digit',
            month: 'long',
            year: 'numeric',
        });

    const shouldShowMarkPeriodEnd = Boolean(isFirstDay && periodForDay && !periodForDay.fecha_termino);

    return (
        <>
            {/* Sección para ingresar/modificar sangrado */}
            <section>
                <h3>Sangrado</h3>
                {/* Si no hay periodo, mostrar opciones según reglas */}
                {!periodForDay && (
                    <>
                        {periodAction.action === 'extend' && periodAction.period && (
                            <div className='calendar-day-modal-actions'>
                                <button className={`btn btn-primary btn-block${isSavingPeriod ? ' btn-loading' : ''}`}
                                    onClick={() => onExtendPeriod({
                                        period: periodAction.period!,
                                        direction: periodAction.direction === 'backward' ? 'backward' : 'forward',
                                    })}
                                    disabled={isSavingPeriod}
                                >
                                    {isSavingPeriod
                                        ? 'Procesando...'
                                        : 'Extender el periodo hasta este día'
                                    }
                                </button>
                            </div>
                        )}
                        {periodAction.action === 'ask' && periodAction.period && (
                            <div className='calendar-day-modal-actions' style={{ marginTop: 10 }}>
                                <div>
                                    <p style={{ marginBottom: 8 }}>
                                        {(() => {
                                            const diff = periodAction.diffDays ?? Math.abs(Math.round((new Date(selectedDate).getTime() - new Date(periodAction.direction === 'backward'
                                                ? periodAction.period!.fecha_inicio
                                                : (periodAction.period!.fecha_termino || periodAction.period!.fecha_inicio)).getTime()) / DAY_IN_MS));
                                            const referenceDate = periodAction.direction === 'backward'
                                                ? periodAction.period!.fecha_inicio
                                                : (periodAction.period!.fecha_termino || periodAction.period!.fecha_inicio);
                                            const formattedReference = formatDateLabel(new Date(referenceDate.split('T')[0] + 'T00:00:00'));
                                            return periodAction.direction === 'backward'
                                                ? `El día seleccionado está a ${diff} ${diff === 1 ? 'día' : 'días'} del inicio del periodo que comienza el ${formattedReference}.`
                                                : `El día seleccionado está a ${diff} ${diff === 1 ? 'día' : 'días'} del fin del periodo anterior (termina el ${formattedReference}).`;
                                        })()}
                                    </p>
                                    <p style={{ marginBottom: 8, marginTop: 0 }}>¿Qué deseas hacer?</p>
                                </div>
                                <button className={`btn btn-primary btn-block${isSavingPeriod ? ' btn-loading' : ''}`}
                                    onClick={() => periodAction.period && onExtendPeriod({
                                        period: periodAction.period,
                                        direction: periodAction.direction === 'backward' ? 'backward' : 'forward',
                                    })}
                                    disabled={isSavingPeriod || !periodAction.period}
                                >
                                    {isSavingPeriod
                                        ? 'Procesando...'
                                        : 'Extender el periodo anterior hasta este día'}
                                </button>
                                <button className={`btn btn-outlined btn-block${isSavingPeriod ? ' btn-loading' : ''}`}
                                    onClick={onCreatePeriod}
                                    disabled={isSavingPeriod}
                                >
                                    {isSavingPeriod ? 'Procesando...' : 'Crear un nuevo periodo'}
                                </button>
                            </div>
                        )}
                        {(periodAction.action === 'create' || periodAction.action === 'associate') && (
                            <div className='calendar-day-modal-actions'>
                                <button className={`btn btn-primary btn-block${isSavingPeriod ? ' btn-loading' : ''}`}
                                    onClick={onCreatePeriod}
                                    disabled={isSavingPeriod}
                                >
                                    {isSavingPeriod ? 'Procesando...' : 'Inicio de nuevo periodo'}
                                </button>
                            </div>
                        )}
                    </>
                )}
                {/* Si hay periodo, permitir editar inicio/fin solo si es primer o último día */}
                {periodForDay && (
                    <div className='calendar-day-modal-actions' style={{ marginTop: 10 }}>
                        {pendingPeriodEdit ? (
                            <div style={{ width: '100%' }}>
                                <div style={{ marginBottom: 12 }}>
                                    <div style={{ fontWeight: 600 }}>{pendingPeriodEdit.title}</div>
                                    <div style={{ marginTop: 6, color: '#4c1d95', lineHeight: 1.4 }}>{pendingPeriodEdit.description}</div>
                                </div>
                                <div style={{ display: 'flex', gap: 8 }}>
                                    <button
                                        className={`btn ${pendingPeriodEdit.isDanger ? 'btn-error' : 'btn-primary'} btn-block${isSavingPeriod ? ' btn-loading' : ''}`}
                                        onClick={onConfirmPendingPeriodEdit}
                                        disabled={isSavingPeriod}
                                    >
                                        {isSavingPeriod ? 'Procesando...' : pendingPeriodEdit.confirmLabel}
                                    </button>
                                    <button
                                        className='btn btn-secondary btn-block'
                                        onClick={onCancelPendingPeriodEdit}
                                        disabled={isSavingPeriod}
                                    >
                                        Cancelar
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <>
                                {shouldShowMarkPeriodEnd && (
                                    <button
                                        className={`btn btn-primary btn-block${isSavingPeriod ? ' btn-loading' : ''}`}
                                        onClick={() => onEditPeriod({})}
                                        disabled={isSavingPeriod}
                                    >
                                        {isSavingPeriod ? 'Procesando...' : 'Marcar este día como fin del periodo'}
                                    </button>
                                )}
                                <button
                                    className={`btn btn-error btn-block${isSavingPeriod ? ' btn-loading' : ''}`}
                                    onClick={() => onEditPeriod({ removeDay: true })}
                                    disabled={isSavingPeriod}
                                    style={{ marginTop: shouldShowMarkPeriodEnd ? 8 : 0 }}
                                >
                                    {isSavingPeriod ? 'Procesando...' : 'Eliminar este día del periodo'}
                                </button>
                            </>
                        )}
                    </div>
                )}
                {isEditingPeriod && (
                    <div style={{ marginTop: 10 }}>
                        <input
                            type='date'
                            value={(() => {
                                const local = new Date(selectedDate.getTime() - selectedDate.getTimezoneOffset() * 60000);
                                return local.toISOString().split('T')[0];
                            })()}
                            disabled
                            style={{ marginBottom: 8 }}
                        />
                    </div>
                )}
            </section>
            {/* Sección para flujo (solo si el día está dentro de un periodo) */}
            {periodForDay && (
                <section>
                    <h3>Flujo</h3>
                    <FlowSelector
                        flujosCatalogo={flujosCatalogo}
                        value={flowLevel}
                        onChange={onChangeFlowLevel}
                    />
                    <div className='calendar-day-modal-actions' style={{ marginTop: 10 }}>
                        <button
                            className={`btn btn-primary btn-block${isSavingFlow ? ' btn-loading' : ''}`}
                            onClick={onSaveFlow}
                            disabled={!flowLevel || isSavingFlow || isSavingSymptoms || isSavingEmotions || isDeleting || isDeletingFlow}
                        >
                            {isSavingFlow
                                ? 'Guardando...'
                                : renderButtonContent(flowSaveStatus, existingFlow ? 'Actualizar flujo' : 'Guardar flujo')}
                        </button>
                        {existingFlow && (
                            <button
                                className={`btn btn-error btn-block${isDeletingFlow ? ' btn-loading' : ''}`}
                                onClick={onDeleteFlow}
                                disabled={isDeletingFlow || isSavingFlow || isSavingSymptoms || isSavingEmotions || isDeleting}
                                style={{ marginTop: 8 }}
                            >
                                {isDeletingFlow ? 'Eliminando...' : 'Eliminar flujo'}
                            </button>
                        )}
                    </div>
                </section>
            )}
            <section>
                <h3>Síntomas</h3>
                <SymptomSelector
                    sintomasCatalogo={sintomasCatalogo}
                    value={selectedSymptoms}
                    onChange={onChangeSymptoms}
                />
                <div className='calendar-day-modal-actions' style={{ marginTop: 10 }}>
                    <button
                        className={`btn btn-primary btn-block${isSavingSymptoms ? ' btn-loading' : ''}`}
                        onClick={onSaveSymptoms}
                        disabled={isSavingSymptoms || isSavingFlow || isSavingEmotions || isDeleting || (!periodForDay && !periodAction.period) || (selectedSymptoms.length === existingSymptoms.length && selectedSymptoms.every(id => existingSymptoms.includes(id)))}
                    >
                        {isSavingSymptoms
                            ? 'Guardando...'
                            : renderButtonContent(symptomsSaveStatus, 'Guardar síntomas')}
                    </button>
                </div>
                {!periodForDay && periodAction.action === 'create' && (
                    <div style={{ color: '#a94442', marginTop: 8 }}>
                        No hay un periodo anterior para asociar los síntomas.
                    </div>
                )}
            </section>
            {/* Sección para emociones */}
            <section>
                <h3>Emociones</h3>
                <EmotionSelector
                    emocionesCatalogo={emocionesCatalogo}
                    value={selectedEmotions}
                    onChange={onChangeEmotions}
                />
                <div className='calendar-day-modal-actions' style={{ marginTop: 10 }}>
                    <button
                        className={`btn btn-primary btn-block${isSavingEmotions ? ' btn-loading' : ''}`}
                        onClick={onSaveEmotions}
                        disabled={isSavingEmotions || isSavingFlow || isSavingSymptoms || isDeleting || (!periodForDay && !periodAction.period) || (selectedEmotions.length === existingEmotions.length && selectedEmotions.every(id => existingEmotions.includes(id)))}
                    >
                        {isSavingEmotions
                            ? 'Guardando...'
                            : renderButtonContent(emotionsSaveStatus, 'Guardar emociones')}
                    </button>
                </div>
                {!periodForDay && periodAction.action === 'create' && (
                    <div style={{ color: '#a94442', marginTop: 8 }}>
                        No hay un periodo anterior para asociar las emociones.
                    </div>
                )}
            </section>
            {periodForDay && (
                <div className='calendar-day-modal-actions' style={{ marginTop: 50, marginBottom: 50 }}>
                    {!showDeleteWarning ? (
                        <button className={`btn btn-error btn-block${isDeleting ? ' btn-loading' : ''}`}
                            onClick={() => onShowDeleteWarning(true)}
                            disabled={isDeleting || isSavingFlow || isSavingSymptoms || isSavingEmotions}
                        >
                            {isDeleting ? 'Procesando...' : 'Eliminar periodo'}
                        </button>
                    ) : (
                        <div style={{ width: '100%' }}>
                            <div style={{ color: '#dc2626', fontWeight: 600, marginBottom: 10 }}>
                                ¿Deseas eliminar este periodo?
                                <br />
                                <span style={{ fontWeight: 400, color: '#a94442' }}>
                                    Se eliminarán también todos los flujos, síntomas y emociones asociados a este periodo.
                                </span>
                            </div>
                            <div style={{ display: 'flex', gap: 8, width: '100%' }}>
                                <button className={`btn btn-error btn-block${isDeleting ? ' btn-loading' : ''}`}
                                    onClick={onDeletePeriod}
                                    disabled={isDeleting || isSavingFlow || isSavingSymptoms || isSavingEmotions}
                                >
                                    {isDeleting ? 'Eliminando...' : 'Confirmar eliminación'}
                                </button>
                                <button className='btn btn-secondary btn-block'
                                    onClick={() => onShowDeleteWarning(false)}
                                    disabled={isDeleting || isSavingFlow || isSavingSymptoms || isSavingEmotions}
                                >
                                    Cancelar
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}
            {error && <div className='error-message'>{error}</div>}
        </>
    );
};

export default CalendarDayModal;

